import { ASSET_SOURCE_PIXELS_PER_METRE } from './asset-source-scale.js';
import type { PlayableWeaponId } from './weapon-progression-v0.js';

/**
 * Presentation-only weapon skin sprites (rpg_weapon_skin_pack_v1 manifest).
 * Pixel coordinates are in the source PNG, which is drawn tip-up. These values
 * never feed reach, contact, collision, damage or acquisition code.
 */
export type WeaponSkinSpriteKey =
  | Exclude<PlayableWeaponId, 'DAGGER_PAIR'>
  | 'DAGGER_PAIR_LEFT'
  | 'DAGGER_PAIR_RIGHT';

export interface WeaponSkinSprite {
  readonly file: string;
  /** CENTER_TO_TIP_REACH spans pivot→tip; OVERALL_ART_LENGTH is drawn at source scale. */
  readonly geometryMode: 'CENTER_TO_TIP_REACH' | 'OVERALL_ART_LENGTH';
  readonly renderLengthM: number;
  readonly canvasPx: readonly [number, number];
  readonly tipPx: readonly [number, number];
  readonly pivotPx: readonly [number, number];
  readonly mountForwardM: number;
  readonly mountLateralM: number;
  /** Rotation that maps the raster's long axis onto the +X aim direction. */
  readonly artRotationRad: number;
  /** OVERALL_ART_LENGTH only: display multiple of the source-scale art (1 = source scale). */
  readonly artScale: number;
}

const TIP_UP_TO_FORWARD = Math.PI / 2;

const melee = (file: string, renderLengthM: number, canvasPx: readonly [number, number],
  pivotX: number, mountLateralM = 0): WeaponSkinSprite => Object.freeze<WeaponSkinSprite>({
  file, geometryMode: 'CENTER_TO_TIP_REACH', renderLengthM, canvasPx,
  tipPx: [pivotX, 12], pivotPx: [pivotX, canvasPx[1] - 13],
  mountForwardM: 0, mountLateralM, artRotationRad: TIP_UP_TO_FORWARD, artScale: 1,
});
/** Catalysts are shown at twice source scale; at 1x the staff read smaller than the player marker. */
const CATALYST_ART_SCALE = 2;
const catalyst = (file: string, canvasPx: readonly [number, number], centerX: number): WeaponSkinSprite => Object.freeze<WeaponSkinSprite>({
  file, geometryMode: 'OVERALL_ART_LENGTH', renderLengthM: 0.4 * CATALYST_ART_SCALE, canvasPx,
  tipPx: [centerX, 12], pivotPx: [centerX, 192.4],
  mountForwardM: 0.34, mountLateralM: 0, artRotationRad: TIP_UP_TO_FORWARD, artScale: CATALYST_ART_SCALE,
});

export const WEAPON_SKIN_SPRITES_V1: Readonly<Record<WeaponSkinSpriteKey, WeaponSkinSprite>> = Object.freeze({
  DAGGER_PAIR_LEFT: melee('dagger_pair_left.png', 1.5, [235, 793], 117, -0.12),
  DAGGER_PAIR_RIGHT: melee('dagger_pair_right.png', 1.5, [235, 793], 117, 0.12),
  SWORD: melee('sword.png', 2.2, [329, 1151], 164),
  HEAVY_AXE: melee('heavy_axe.png', 2.75, [666, 1433], 332.5),
  SPEAR: melee('spear.png', 3.75, [145, 1945], 72),
  ASSASSIN_BLADE: melee('assassin_blade.png', 1.5, [180, 793], 89.5),
  GREATSWORD: melee('greatsword.png', 2.8, [444, 1459], 221.5),
  // The bow's long axis stays perpendicular to aim, pivoting on the handle center.
  // The raster draws its string on +X, so a half turn puts the limbs toward
  // the target and the string toward the archer.
  BOW_REFERENCE: Object.freeze<WeaponSkinSprite>({
    file: 'bow_reference.png', geometryMode: 'OVERALL_ART_LENGTH', renderLengthM: 1.4, canvasPx: [137, 742],
    tipPx: [68, 12], pivotPx: [68, 370.5], mountForwardM: 0.28, mountLateralM: 0, artRotationRad: Math.PI, artScale: 1,
  }),
  ARCANE_CATALYST: catalyst('arcane_catalyst.png', [63, 230], 31),
  BLAST_CATALYST: catalyst('blast_catalyst.png', [108, 230], 53.5),
  RAPID_CATALYST: catalyst('rapid_catalyst.png', [116, 230], 57.5),
});

export const WEAPON_SKIN_ASSET_FILES: readonly string[] = Object.freeze(
  Object.values(WEAPON_SKIN_SPRITES_V1).map((sprite) => sprite.file));

/** Source pixels → CSS pixels. Center-to-tip sprites stretch so their tip lands on `reachM`; other art is source scale × `artScale`. */
export function weaponSkinScale(sprite: WeaponSkinSprite, pixelsPerMetre: number, reachM?: number): number {
  return sprite.geometryMode === 'CENTER_TO_TIP_REACH' && reachM !== undefined
    ? reachM * pixelsPerMetre / (sprite.pivotPx[1] - sprite.tipPx[1])
    : pixelsPerMetre / ASSET_SOURCE_PIXELS_PER_METRE * (sprite.geometryMode === 'OVERALL_ART_LENGTH' ? sprite.artScale : 1);
}
